import * as jotai from 'jotai'
import * as React from 'react'

import * as api from '@owl-nest/api-client/next'
import * as env from './env/base.ts'
import { lens } from './lens.ts'
import { getCookie } from '@owl-nest/cookie-helper'

export type UserConfig = {
  lang: api.Lang
  currency: string
}

let INITIALIZED = false
const USER_CONFIG_ATOM = jotai.atom<UserConfig>({
  lang: env.DEFAULT_LOCALE,
  currency: env.DEFAULT_CURRENCY,
})

export async function initUserConfig(): Promise<void> {
  if (!INITIALIZED) {
    INITIALIZED = true

    const userLocale = getUserLocale() ?? env.DEFAULT_LOCALE
    const userCurrency = getUserCurrency() ?? env.DEFAULT_CURRENCY

    const store = jotai.getDefaultStore()

    store.set(USER_CONFIG_ATOM, {
      lang: userLocale,
      currency: userCurrency,
    })
  }
}

export function getUserConfig(): UserConfig {
  const store = jotai.getDefaultStore()
  initUserConfig()
  return store.get(USER_CONFIG_ATOM)
}

export function useUserEnv() {
  const config = jotai.useAtomValue(USER_CONFIG_ATOM)

  React.useEffect(() => {
    initUserConfig()
  }, [])

  return config
}

export function getUserLocale(): api.Lang | undefined {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search)
    const langFromUrl = urlParams.get('lang')
    if (langFromUrl !== undefined && env.LANGS.includes(langFromUrl as api.Lang)) {
      return langFromUrl as api.Lang
    }
  }

  const ufeUserLocale = lens<env.UfeConfig>()
    .get('UFE')
    .get('locale')
    .value(globalThis, { silent: true, forgive: true, trust: true })
  if (ufeUserLocale !== undefined) {
    return ufeUserLocale
  }

  const cookieUserLocale = getCookie(env.USER_LANG_COOKIE_NAME)
  if (cookieUserLocale !== undefined && env.LANGS.includes(cookieUserLocale)) {
    return cookieUserLocale as api.Lang
  }

  if (typeof window === 'undefined') {
    return env.DEFAULT_LOCALE
  }

  const subdomain = window.location.host.split('.')[0]

  const locale = (Object.keys(env.I18N_SUBDOMAINS) as api.Lang[]).find((locale) => {
    return env.I18N_SUBDOMAINS[locale] === subdomain
  })

  return locale
}

export function getUserCurrency() {
  const ufeUserCurrency = lens<env.UfeConfig>()
    .get('CURRENCIES')
    .get('CURRENT')
    .value(globalThis, { silent: true, forgive: true, trust: true })
  if (ufeUserCurrency !== undefined) {
    return ufeUserCurrency
  }

  const cookieUserCurrency = getCookie(env.USER_CURRENCY_COOKIE_NAME)
  return cookieUserCurrency
}
